import { useContext } from 'react'
import { FormattedMessage, useIntl } from 'react-intl'
import List from 'components/Common/List/List'
import BodyText from 'LuxKit/Typography/BodyText'
import Heading from 'LuxKit/Typography/Heading'
import { depositAmountStringBuilder } from 'lib/tours/tourUtils'
import VerticalSpacer from '../Spacing/VerticalSpacer'
import { LE_CONTRACTED_HOTEL_TYPES, OFFER_TYPE_ALWAYS_ON, OFFER_TYPE_TOUR } from 'constants/offer'
import { getMinDepositAmount, isOfferDefaultDepositAmountPercentage } from 'lib/payment/depositsUtils'
import FormatCurrency from '../FormatCurrency'
import GeoContext from 'contexts/geoContext'
import { INELEGIBLE_SERVICE_FEE_OFFER_TYPES } from 'constants/payment'

import { getDefaultDepositAmountPercentage, getDepositServiceFee } from 'checkout/selectors/featureConfig/deposit'
import { useAppSelector } from 'hooks/reduxHooks'
import { TOUR_V2_OFFER_DUE_DATE_DAY_THRESHOLD } from 'constants/tours'
import { isTourV2OfferType } from 'lib/offer/offerTypes'

// HOW IS THIS DIFFERENT TO getPaymentDueDate IN LIB/DEPOSITUTILS?
function getPaymentDueDaysBeforeCheckin(
  offerType: App.OfferType,
  depositThresholds?: App.DepositThreshold,
) {
  if (depositThresholds?.numberOfDays) {
    return depositThresholds.numberOfDays
  }

  return (offerType == OFFER_TYPE_TOUR || isTourV2OfferType(offerType)) ? TOUR_V2_OFFER_DUE_DATE_DAY_THRESHOLD : 60
}

function getOfferEligibilityDaysInAdvance(
  offerType: App.OfferType,
  depositThresholds?: App.DepositThreshold,
) {
  if (depositThresholds?.numberOfDays) {
    return depositThresholds.numberOfDays + 1
  }

  return (offerType === OFFER_TYPE_TOUR || isTourV2OfferType(offerType)) ? TOUR_V2_OFFER_DUE_DATE_DAY_THRESHOLD + 1 : 61
}

function getOfferTypeStringKey(offerType: App.OfferType): 'tour' | 'lux_premium' | 'lux_exclusive' {
  switch (offerType) {
    case OFFER_TYPE_TOUR:
    case 'direct_tour':
    case 'partner_tour':
    case 'connection_tour':
      return 'tour'
    case OFFER_TYPE_ALWAYS_ON:
      return 'lux_premium'
    default:
      return 'lux_exclusive'
  }
}

export interface Props {
  offerType: App.OfferType
  offerPackage?: App.Package
  pkgAvailableRate?: App.OfferAvailableRate
  checkIn?: moment.Moment
  depositAmountPercentage?: number
  depositThresholds?: App.DepositThreshold
  depositType?: App.Tours.V2OfferDepositType
  isFlexibleCancellation?: boolean
  depositAmount?: number
}

function DepositInfoModalContent({
  offerType,
  depositAmountPercentage,
  depositThresholds,
  depositType,
  isFlexibleCancellation,
  depositAmount,
}: Props) {
  const intl = useIntl()
  const { currentCurrency } = useContext(GeoContext)
  const offerEligibilityDaysInAdvance = getOfferEligibilityDaysInAdvance(offerType, depositThresholds)
  const paymentDueDaysBeforeCheckin = getPaymentDueDaysBeforeCheckin(offerType, depositThresholds)
  const isHotel = LE_CONTRACTED_HOTEL_TYPES.includes(offerType)
  const minAmount = getMinDepositAmount(currentCurrency)

  const formattedMinimumAmount = <FormatCurrency value={minAmount} format="roundedDollar" currency={currentCurrency} />

  const offerTypeKey = getOfferTypeStringKey(offerType)
  // TODO - i18n: localise LE offer types
  const offerTypeStringMap = {
    tour: intl.formatMessage({
      id: '_lip3u__lmsG0Rq6N9y3',
      defaultMessage: 'Tour',
      description: 'Offer type label for tour bookings on the tour offer detail page',
    }),
    lux_premium: intl.formatMessage({
      id: 'HOtO81qRv8eaQpjihojh',
      defaultMessage: 'Lux Premium Collection',
      description: 'Offer type label for Lux Premium Collection bookings',
    }),
    lux_exclusive: intl.formatMessage({
      id: 'ltcl9shPJdBBD4iSZggJ',
      defaultMessage: 'Limited Time LUX Exclusive',
      description: 'Offer type label for limited time exclusive bookings',
    }),
  }
  const offerTypeString = offerTypeStringMap[offerTypeKey]

  const defaultDepositAmountPercentage = useAppSelector(getDefaultDepositAmountPercentage)
  const depositServiceFee = useAppSelector(getDepositServiceFee)
  // Enabled for a offer type as long as it is not a connection_tour v2 offer
  const shouldDisplayServiceFeeMessage: boolean = !INELEGIBLE_SERVICE_FEE_OFFER_TYPES.includes(offerType)

  const isOfferUsingDefaultDepositPercentage = isOfferDefaultDepositAmountPercentage(depositThresholds, depositAmount)

  const depositAmountString = isOfferUsingDefaultDepositPercentage ?
    'deposit' : !depositType ? `${depositAmountPercentage}% deposit` : `${depositAmountStringBuilder(defaultDepositAmountPercentage, depositType, depositAmountPercentage, intl).short} deposit`

  return (
    <VerticalSpacer gap={16}>
      <BodyText variant="large" as="p">
        <FormattedMessage
          id="lMdAdBMATl8daJZ4HuHH"
          defaultMessage="Just pay a {depositDescription} for this offer now, and the rest later {dayCount} days prior to your check-in date."
          description="Deposit payment summary explaining deposit amount and when the balance is due"
          values={{ depositDescription: depositAmountString, dayCount: paymentDueDaysBeforeCheckin }}
        />
      </BodyText>
      <div>
        <Heading variant="heading6">
          {intl.formatMessage({
            id: 'lCa01Ax4C2aXAEvX1qWy',
            defaultMessage: 'Eligibility',
            description: 'Section heading for deposit eligibility section in a modal dialog',
          })}
        </Heading>
        <BodyText variant="large" as="p">
          {isHotel && minAmount > 0 && <FormattedMessage
            id="m8Yv9JWLi-R86Dx39gnu"
            defaultMessage="{offerType} bookings (excluding flights, experiences, travel protection) with a value over {minimumAmount} and travel dates at least {days} days in advance."
            description="Deposit eligibility criteria for hotel bookings with minimum amount requirement"
            values={{ offerType: offerTypeString, minimumAmount: formattedMinimumAmount, days: offerEligibilityDaysInAdvance }}
          />}
          {isHotel && !(minAmount > 0) && <FormattedMessage
            id="rSfVd3VraHSc1V_yjvaL"
            defaultMessage="{offerType} bookings (excluding flights, experiences, travel protection) with travel dates at least {days} days in advance."
            description="Deposit eligibility criteria for hotel bookings without minimum amount requirement"
            values={{ offerType: offerTypeString, days: offerEligibilityDaysInAdvance }}
          />}
          {!isHotel && minAmount > 0 && <FormattedMessage
            id="QmxzuLO6V2JNKyKy33hv"
            defaultMessage="{offerType} bookings with a value over {minimumAmount} and travel dates at least {days} days in advance."
            description="Deposit eligibility criteria for non-hotel bookings with minimum amount requirement"
            values={{ offerType: offerTypeString, minimumAmount: formattedMinimumAmount, days: offerEligibilityDaysInAdvance }}
          />}
          {!isHotel && !(minAmount > 0) && <FormattedMessage
            id="NtlmSE-fH6zlBZ71EMsk"
            defaultMessage="{offerType} bookings with travel dates at least {days} days in advance."
            description="Deposit eligibility criteria for non-hotel bookings without minimum amount requirement"
            values={{ offerType: offerTypeString, days: offerEligibilityDaysInAdvance }}
          />}
        </BodyText>
      </div>
      <div>
        <Heading variant="heading6">
          {intl.formatMessage({
            id: 'HeIgZjo-d_Eux9hioMAW',
            defaultMessage: 'Cancellation policy',
            description: 'Section heading for deposit cancellation policy section',
          })}
        </Heading>
        <BodyText variant="large" as="p">
          {isFlexibleCancellation && <FormattedMessage
            id="BkPQxj6I_SmHIq9hmJ2h"
            defaultMessage="Deposit is non-refundable after free cancellation period. Failure to complete payment will result in the cancellation of your booking, your deposit less the service fee owed will be defaulted to short-dated credits, expiring 90 days after your booking has been cancelled."
            description="Cancellation policy text for flexible cancellation deposits"
          />}
          {!isFlexibleCancellation && <FormattedMessage
            id="XBFnotcFuVeRsJeWliZ7"
            defaultMessage="Deposit is non-refundable after free cancellation period. Failure to complete payment will result in the cancellation of your booking, and forfeit of the deposit."
            description="Cancellation policy text for non-flexible cancellation deposits"
          />}
        </BodyText>
      </div>
      {isHotel && <BodyText variant="large" as="p">
        <FormattedMessage
          id="CoY2u62lVHtcVsTd2iNi"
          defaultMessage="Deposit refunds apply only to accommodation. Flights and experiences are subject to the refund policies of the respective operator"
          description="Note that deposit refunds only cover accommodation, not flights or experiences"
        />
      </BodyText>}
      {shouldDisplayServiceFeeMessage && <div>
        <BodyText variant="large" as="p">
          <FormattedMessage
            id="2-EC-6owp5ocvyd89u_e"
            defaultMessage="The service fee paid as part of a deposit booking is non-refundable even if cancellation occurs within 7 days of purchase"
            description="Warning that the service fee on deposit bookings is non-refundable"
          />
        </BodyText>
      </div>}
      <BodyText variant="medium" as="p">
        <Heading variant="heading6">
          {intl.formatMessage({
            id: 'QH_LwYHFSOgcy6Pczh4D',
            defaultMessage: 'Changes to booking',
            description: 'Heading for the changes to booking section in deposit info',
          })}
        </Heading>
        <BodyText variant="medium" underline as="p">
          {intl.formatMessage({
            id: 'ICr-opfDhYjY7HyiuH0x',
            defaultMessage: 'Moving booking to an earlier date:',
            description: 'Subheading for rules about moving a deposit booking to an earlier date',
          })}
        </BodyText>
        <List type="ul">
          <BodyText variant="medium" as="li">
            {intl.formatMessage({
              id: 'm9bvsoe1D2IMtD03YcFj',
              defaultMessage: 'Within 60 days of check-in:',
              description: 'List item label for changes within 60 days of check-in',
            })}
          </BodyText>
          <List type="ul">
            <BodyText variant="medium" as="li">
              {intl.formatMessage({
                id: 'xDu4a_pS5T7_OHdDEBV2',
                defaultMessage: 'The entire balance must be paid at the time of making the change, regardless of any price adjustments.',
                description: 'Rule for balance payment when changing booking within 60 days',
              })}
            </BodyText>
          </List>
        </List>
        <List type="ul">
          <BodyText variant="medium" as="li">
            {intl.formatMessage({
              id: '-aYSVK5e7tftSNsT1pU5',
              defaultMessage: 'More than 60 days from check-in:',
              description: 'List item label for changes more than 60 days from check-in',
            })}
          </BodyText>
          <List type="ul">
            <BodyText variant="medium" as="li">
              {intl.formatMessage({
                id: 'FTl6UvJzRb7ajCoCcz8d',
                defaultMessage: 'If there is a price change: the entire balance must be paid at the time of the change.',
                description: 'Rule for balance payment when price changes and booking is more than 60 days out',
              })}
            </BodyText>
            <BodyText variant="medium" as="li">
              {intl.formatMessage({
                id: 'posD1eoQjvNj0p9IS_js',
                defaultMessage: 'If there is no price change: you can move the booking without any additional payment.',
                description: 'Rule when no price change and booking is more than 60 days out',
              })}
            </BodyText>
          </List>
        </List>
        <BodyText variant="medium" underline as="p">
          {intl.formatMessage({
            id: '6zg5laIB8zrRgBmNYX9f',
            defaultMessage: 'Moving booking to a future date:',
            description: 'Subheading for rules about moving a deposit booking to a future date',
          })}
        </BodyText>
        <List type="ul">
          <BodyText variant="medium" as="li">
            {intl.formatMessage({
              id: 'sewYCeosZGzRSgqyWc7C',
              defaultMessage: 'The original deposit remains valid for the new booking.',
              description: 'Rule that original deposit carries over to the new booking date',
            })}
          </BodyText>
          <BodyText variant="medium" as="li">
            {intl.formatMessage({
              id: 'VjpYLUDqFRzpfTGhML6H',
              defaultMessage: 'If there is a price change: you only pay the difference in price.',
              description: 'Rule for price difference when moving booking to a future date',
            })}
          </BodyText>
          <BodyText variant="medium" as="li">
            {intl.formatMessage({
              id: 's6H-B_7wZjSpqX9ov3PE',
              defaultMessage: 'If there is no price change: you can move the booking without any additional payment.',
              description: 'Rule when no price change and moving booking to a future date',
            })}
          </BodyText>
        </List>
      </BodyText>
      {shouldDisplayServiceFeeMessage && <div>
        <Heading variant="heading6">
          {intl.formatMessage({
            id: 'XgJCKcIC4AxafBIX2uFg',
            defaultMessage: 'Service Fee',
            description: 'Heading for the service fee section in deposit info',
          })}
        </Heading>
        <BodyText variant="large" as="p">
          <FormattedMessage
            id="fuByV1AROI8iiYun5nMy"
            defaultMessage="A service fee of {serviceFeePercentage}% of the balance of the purchase price payable will be charged at the time of purchase (at the same time the initial {depositDescription} is paid)."
            description="Service fee explanation for deposit bookings, showing fee percentage and when it is charged"
            values={{ serviceFeePercentage: depositServiceFee, depositDescription: depositAmountString }}
          />
        </BodyText>
      </div>}
    </VerticalSpacer>
  )
}

export default DepositInfoModalContent
